import { Box, Link, styled } from '@mui/material';
import { Link as RouterLink } from 'react-router';

import useBrainSession from '@/services/BrainSession/useBrainSession.ts';

import { SIZES } from '../../assets/constants/constants.ts';
import ActionButton from '../../components/ActionButton/index.ts';
import Breadcrumbs from '../../components/Breadcrumbs/index.ts';
import Page from '../../components/Page/index.ts';
import RrsHandler from '../../components/RrsHandler/index.ts';
import Section from '../../components/Section/index.ts';
import { LANG } from '../../services/Config.js';
import Disclaimer from '../Common/Disclaimer.tsx';

import { RRS_COMPARE_LOANS } from './RRS.ts';

const ArticleSectionsContainer = styled('div')(({ theme }) => ({
  maxWidth: SIZES.layout.width.sm,
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const TopSection = styled('div')(() => ({
  maxWidth: SIZES.layout.width.sm,
  display: 'flex',
  flexDirection: 'column',
  '& > button': {
    alignSelf: 'center',
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  ...theme.typography.p3,
})) as typeof Link;

const StyledList = styled('ul')(({ theme }) => ({
  ...theme.typography.p3,
  paddingLeft: theme.spacing(3.5),
  '& > li': {
    marginBottom: theme.spacing(1.5),
  },
}));

type CompareLoansProps = Record<string, never>;

const CompareLoans: React.FC<CompareLoansProps> = () => {
  const { appStrings, routes } = LANG;
  const { toggleChat } = useBrainSession();

  return (
    <Page title={appStrings.MENU.COMPARE_LOANS}>
      <RrsHandler {...RRS_COMPARE_LOANS} />
      <Section fullWidth>
        <TopSection>
          <Box
            sx={{
              mb: 4,
            }}
          >
            <Breadcrumbs
              items={[
                { text: appStrings.MENU.HOME, url: routes.HOME },
                {
                  text: appStrings.MENU.MY_ECONOMY,
                  url: routes.MY_ECONOMY,
                },
                { text: appStrings.MENU.COMPARE_LOANS },
              ]}
            />
          </Box>
          <h1 className="mb-6 typography-v1-legacy-h2 text-v1-primary-100 sm:mb-8">
            Sammenligne lån? Vi har gjort jobben for deg!
          </h1>
          <h2 className="mb-4 typography-v1-legacy-subtitle1Bold">
            Når du skal sammenligne lån finnes det mange muligheter. Faktisk så mange at du
            risikerer å gå glipp av den beste renten du kan få, fordi du ikke fant banken "din". Det
            har vi i Scoopr gjort noe med – når du søker om lån hos oss går søknaden din automatisk
            ut til 13 banker, som konkurrerer om å gi deg den beste renten.
          </h2>
          <p className="mb-8 typography-v1-legacy-subtitle2 sm:mb-14">
            Er det én ting vi er opptatt av når vi søker om lån, så er det å få lavest mulig rente.
            Om du tror du har funnet banken som gir deg det beste tilbudet, finnes det likevel mange
            feller du kan gå i.
            <br />
            <br />
            Det er nemlig visse ting du bør kjenne til når du skal sammenligne lån, så du ikke ender
            opp med å betale mye mer enn du behøver.
          </p>
          <ActionButton
            label="Sjekk hvor mye du kan spare nå!"
            flow="debt_check"
            step="skip_start"
          />
        </TopSection>
      </Section>
      <Section fullWidth background="paper" last>
        <ArticleSectionsContainer>
          <Box
            sx={{
              pr: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            {/* 1 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Sammenlign lån – smarte tips før du søker
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Skal du søke om lån, er det alltid smart å sammenligne pris. Det kan bety tusenvis av
              kroner – hver måned. I tillegg bør du ta en titt på økonomien din før du sender
              søknaden.
            </p>

            {/* 2 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Sjekk renten du får</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Når du surfer på nettet og sammenligner ett og ett lån, er det gjerne den nominelle
              renten som blir markedsført med store bokstaver. Du må kjenne til{' '}
              <StyledLink component={RouterLink} to={routes.INTEREST_RATE} underline="hover">
                hva den effektive renten er
              </StyledLink>
              , da denne inkluderer alle kostnader og gebyrer, og disse kan variere stort fra bank
              til bank.
              <br />
              <br />
              Husk også at eksempelrenten som vises er gjennomsnittet av innvilgede lån. Hvilken
              rente du får avhenger av privatøkonomien din og hvor stort beløpet er. Har du god
              økonomi og søker om et lite lån, får du lavere rente enn om du har en trangere økonomi
              og søker om et større lån.
            </p>

            {/* 3 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Bruk en lånekalkulator</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Da kan du enkelt sammenligne hva lånet koster deg. Forbrukslånskalkulatoren hos Scoopr
              viser deg alle kostnader på renter og gebyrer, slik at du ser hva du må betale. Her
              kan du også prøve deg frem og se hva ulike beløp, renter og nedbetalingstid utgjør per
              måned.
            </p>

            {/* 4 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Finn ut hvor mye kan du betale
            </h3>
            <p className="typography-v1-legacy-p3">
              Vær nøye når du regner ut hva du må betale per måned. Sett gjerne opp et budsjett, og
              se spesielt på "lommetyvene" dine. Kanskje du kjenner deg igjen i disse?
            </p>

            <StyledList>
              <li>Kutt kaffen på vei til jobb, take away-middager og bar- og restaurantbesøk.</li>
              <li>
                Lag en handleliste, hold deg til den og dra på butikken maks to ganger i uken.
              </li>
              <li>TV-abonnementene dine kan fort utgjøre langt mer enn en tusenlapp i måneden.</li>
              <li>
                Røyk og snus er både dyrt og skadelig, så å slutte er bra for økonomien og helsen.
              </li>
            </StyledList>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Du finner garantert flere overraskende utgifter dersom du sjekker bankkontoen din.
            </p>
          </Box>

          <Box
            sx={{
              pl: { xs: 0, sm: 3 },
              width: { xs: '100%', sm: '50%' },
            }}
          >
            {/* 5 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Vær sikker på at du klarer nedbetalingsplanen
            </h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Dersom du ikke holder deg til og følger nedbetalingsplanen, kan lånet få høyere
              kostnader enn økonomien din tåler. I verste fall kan du ende opp med at lånet blir
              misligholdt. Da risikerer du betalingsanmerkninger, som fører til at du ikke engang
              får mobilabonnement. Og i heller ikke nye lån og kredittkort.
              <br />
              <br />
              Visste du forresten at{' '}
              <StyledLink component={RouterLink} to={routes.REPAYMENT_PERIOD} underline="hover">
                jo raskere du klarer å nedbetale, desto billigere blir lånet ditt?
              </StyledLink>
            </p>

            {/* 6 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">Bruk en seriøs bank</h3>
            <p className="mb-8 typography-v1-legacy-p3 sm:mb-14">
              Dette kan høres merkelig ut, men her er det mange som har brent seg. Det er nemlig
              noen aktører som ikke er den de utgir seg for å være. Kanskje du finner en bank som
              tilbyr renter som ligger langt under de andre? Det er dessverre ofte slik at hvis noe
              er for godt til å være sant, så er det gjerne det.
              <br />
              <br />
              Hvis du skulle være i tvil – alle samarbeidsbankene til Scoopr er selvsagt seriøse og
              er godkjent av Finanstilsynet.
              <br />
              <br />
              <strong>
                Alt dette kan være vanskelig å finne ut av på egenhånd. Derfor er det smart å bruke
                sammenligningstjenesten Scoopr – da kan du være helt trygg på at du får de beste
                betingelsene.
              </strong>
            </p>

            {/* 7 */}
            <h3 className="mb-4 typography-v1-legacy-subtitle1Bold">
              Scoopr sammenligner lån for deg
            </h3>
            <p className="typography-v1-legacy-p3">
              Det betyr at du sparer tid og penger. Du slipper å surfe rundt i timer og dager, og
              sende søknad til én og én bank – vi har funnet de bankene vi vet vil gi deg det beste
              tilbudet du kan få, og det tjener du på.
              <br />
              <br />
              Det er enkelt, gratis og uforpliktende å søke, og du gjør det på 1-2-3:
              <br />
              <br />
              <strong>1 Søk</strong>
              <br />
              Finn frem BankID, og{' '}
              <StyledLink
                component="button"
                onClick={() =>
                  toggleChat({
                    goal: 'debt_check',
                    step: 'refin_present_terms_auto_new',
                  })
                }
                underline="hover"
              >
                trykk her
              </StyledLink>
              . Du kan søke om lån fra 10.000 til 600.000 kroner. Når du har sendt søknaden er
              jobben din gjort – da sørger vi for at den går automatisk til bankene
              <br />
              <br />
              <strong>2 Sammenlign</strong>
              <br />
              Når du har mottatt tilbud, vurderer du selv hvilket som passer deg og økonomien din
              best. Du trenger ikke å forhaste deg – tilbudene gjelder i 30 dager.
              <br />
              <br />
              <strong>3 Velg</strong>
              <br />
              Etter at du har sammenlignet, velger du bank. Når du har gjort det, er som regel
              pengene på konto i løpet av et par dager. Og dersom du{' '}
              <StyledLink component={RouterLink} to={routes.PRODUCTS_REFINANCING} underline="hover">
                refinansierer
              </StyledLink>
              , betaler vanligvis banken den gamle gjelden til kreditorene dine.
              <br />
              <br />
              <strong>
                Ønsker du at Scoopr sammenligner lån og priser for deg?{' '}
                <StyledLink
                  component="button"
                  onClick={() =>
                    toggleChat({
                      goal: 'debt_check',
                      step: 'refin_present_terms_auto_new',
                    })
                  }
                  underline="hover"
                >
                  Det tar deg kun to minutter å søke.
                </StyledLink>
              </strong>
            </p>
          </Box>
        </ArticleSectionsContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 10,
          }}
        >
          <ActionButton label="Sjekke mye du kan spare nå!" flow="debt_check" step="skip_start" />
          <Disclaimer />
        </Box>
      </Section>
    </Page>
  );
};

export default CompareLoans;
